import { AppVersion, ApiResponse, Platform } from '@/types/version';

// API 基础地址
const API_BASE_URL = import.meta.env.VITE_ESTHA_API_BASE_URL || 'http://127.0.0.1:8082';

/**
 * 获取最新版本信息
 * @param platform 平台类型
 * @returns 最新版本信息
 */
export async function getLatestVersion(platform: Platform = Platform.MacOSARM64): Promise<AppVersion> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/version/latest?platform=${platform}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result: ApiResponse<AppVersion> = await response.json();

    if (result.status !== 200) {
      throw new Error(result.msg || '获取版本信息失败');
    }

    return result.data;
  } catch (error) {
    console.error('获取最新版本失败:', error);
    throw error;
  }
}

/**
 * 检测用户的 macOS 平台类型
 * @returns 平台类型
 */
export function detectPlatform(): Platform {
  if (typeof window === 'undefined') {
    return Platform.MacOSARM64;
  }

  const userAgent = navigator.userAgent.toLowerCase();
  const isMac = /macintosh|mac os x/i.test(userAgent);

  if (!isMac) {
    // 默认返回 Universal 版本
    return Platform.MacOSARM64;
  }

  // 检测是否为 Apple Silicon (ARM64)
  // 注意：在浏览器中无法直接检测 CPU 架构，这里使用一些启发式方法
  // 实际上大部分情况下应该返回 Universal 版本，让用户自己选择
  return Platform.MacOSARM64;
}

/**
 * 格式化文件大小
 * @param bytes 字节数
 * @returns 格式化后的文件大小字符串
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * 触发文件下载
 * @param url 下载链接
 * @param filename 文件名
 */
export function downloadFile(url: string, filename?: string): void {
  const link = document.createElement('a');
  link.href = url;
  if (filename) {
    link.download = filename;
  }
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
